'use client';

import { useMemo } from 'react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { useI18n, useCurrentLocale } from '@locales/client';
import { format, isThisYear } from 'date-fns';

import { fetcherWithToken } from '@/shared/api/fetcher';
import { getDateLocale } from '@/shared/libs/locale';

import { timestampToDate } from '@/features/wallet-managment/model/utils/groupTransactionsByDate';

export const useNotifyList = () => {
  const {
    data: notifyList,
    isLoading: isNotifyListLoading,
    error: notifyListError,
    mutate: notifyListMutate,
  } = useSWR('/notifications/list', fetcherWithToken);
  const { trigger } = useSWRMutation(
    '/notifications/markRead',
    fetcherWithToken
  );

  return {
    notifyList,
    markReadAllTrigger: trigger,
    notifyListMutate,
    isNotifyListLoading: isNotifyListLoading,
    notifyListError: notifyListError,
  };
};

export const useNotificationDate = (notifyList) => {
  const t = useI18n();
  const locale = useCurrentLocale();

  const formatGroupHeader = (date: Date) => {
    // if (isToday(date)) return t('today');
    // if (isYesterday(date)) return t('yesterday');

    if (isThisYear(date)) {
      return format(date, 'd MMMM', { locale: getDateLocale(locale) });
    }

    return format(date, 'd MMMM yyyy', { locale: getDateLocale(locale) });
  };

  const formatNotificationTime = (date: Date) => {
    return format(date, 'HH:mm', { locale: getDateLocale(locale) });
  };

  const formatFullDate = (date: Date) => {
    return format(date, 'd MMMM, HH:mm', { locale: getDateLocale(locale) });
  };

  // Группировка уведомлений по дате
  const groupedNotifications = useMemo(() => {
    if (!Array.isArray(notifyList)) return [];

    const groups: Record<string, { date: Date; items: any[] }> = {};

    notifyList.forEach((item) => {
      const date = timestampToDate(item.ts);
      const normalizedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );

      const groupKey = normalizedDate.toISOString();

      if (!groups[groupKey]) {
        groups[groupKey] = {
          date: normalizedDate,
          items: [],
        };
      }

      groups[groupKey].items.push(item);
    });

    // Сортируем группы по дате (новые сверху)
    return Object.values(groups).sort(
      (a, b) => b.date.getTime() - a.date.getTime()
    );
  }, [notifyList]);

  return {
    groupedNotifications,
    formatGroupHeader,
    formatNotificationTime,
    formatFullDate,
  };
};
